import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useScan } from "../../context/InventoryContext";
import ScannedProductPopup from "../../components/ScannedProductPopup";
import { BASE_URL } from "../../const";
import Navbar from '../../components/Navbar'; 

const ScannedResult = () => {
  const location = useLocation();
  const scannedCode = location.state?.code;
  const { items, setItems, clearItems } = useScan();
  const [eanInput, setEanInput] = useState("");
  const [popupCode, setPopupCode] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get('mode');
  const title = mode === 'grn' ? '📥 GRN Processing Submission' : '📦 Inventory Count Submission';

  const store = localStorage.getItem("store");
  const AUTH_TOKEN = localStorage.getItem("token");

  useEffect(() => {
    if (!scannedCode) {
      console.log("User went to results without scanning");
    }
  }, [scannedCode]);

  const handleQtyChange = (sku, newQty) => {
    const qty = Math.max(1, Math.min(999, Number(newQty) || 1));
    setItems(prevItems => 
      prevItems.map(item => 
        item.sku === sku ? { ...item, qty } : item
      )
    );
  };

  const handleDelete = (sku) => {
    setItems(prevItems => prevItems.filter(item => item.sku !== sku));
  };

  const handleSubmit = async () => {
    if (!store) {
      alert("Missing store info. Please log in again.");
      return;
    }
    
    if (items.length === 0) {
      alert("No items to submit.");
      return;
    }

    setIsSubmitting(true);
    const data = items.map((item) => ({
      store,
      sku: item.sku,
      qty: item.qty,
    }));

    try {
      const res = await fetch(`${BASE_URL}/add-invnentory-grn/${mode}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${AUTH_TOKEN}`,
        },
        body: JSON.stringify(data),
      });

      if (res.ok) {
        alert("Submitted successfully!");
        clearItems();
      } else {
        const errorData = await res.json();
        alert(`Submission failed: ${errorData.message || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Submit error:", err);
      alert("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalQty = items.reduce((sum, item) => sum + item.qty, 0);

  return (
    <>
    <Navbar />
    <div style={styles.container}>
      <h2 style={styles.heading}>{title}</h2>
      <p style={styles.store}>Store: <strong>{store}</strong></p>

      <div style={styles.searchRow}>
        <input
          type="text"
          value={eanInput}
          onChange={(e) => setEanInput(e.target.value)}
          placeholder="Enter EAN manually..."
          style={styles.searchInput}
        />
        <button
          onClick={() => {
            if (eanInput.trim()) setPopupCode(eanInput.trim());
          }}
          style={styles.searchBtn}
          disabled={!eanInput.trim()}
        >
          Search
        </button>
      </div>

      {items.length === 0 ? (
        <p style={styles.empty}>No items scanned.</p>
      ) : (
        <>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Image</th>
                <th style={styles.th}>Details</th>
                <th style={styles.th}>Qty</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={`${item.sku}-${item.ean}`}>
                  <td style={styles.td}>
                    <div style={styles.imageContainer}>
                      {item.image_link ? (
                        <img 
                          src={item.image_link} 
                          alt={item.product_name} 
                          style={styles.image}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                      ) : null}
                      <div style={styles.imagePlaceholder}>
                        {item.product_name?.charAt(0)?.toUpperCase() || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.details}>
                      <div><strong>{item.product_name}</strong></div>
                      <div>EAN: {item.ean}</div>
                      <div>SKU: {item.sku}</div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number"
                      min="1"
                      max="999"
                      value={item.qty}
                      onChange={(e) => handleQtyChange(item.sku, e.target.value)}
                      style={styles.qtyInput}
                    />
                  </td>
                  <td style={styles.td}>
                    <button 
                      onClick={() => handleDelete(item.sku)} 
                      style={styles.deleteBtn}
                      disabled={isSubmitting}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p style={styles.total}>Total Qty: <strong>{totalQty}</strong></p>

          <div style={styles.btnWrap}>
            <button 
              onClick={handleSubmit} 
              style={styles.submitBtn}
              disabled={isSubmitting || items.length === 0}
            >
              {isSubmitting ? (
                <>
                  <span style={styles.spinner}></span>
                  Submitting...
                </>
              ) : "Submit to Backend"}
            </button>
          </div>
        </>
      )}

      {popupCode && (
        <ScannedProductPopup
          code={popupCode}
          onSubmit={() => setPopupCode(null)}
          onClose={() => setPopupCode(null)}
        />
      )}
    </div>
    </>
  );
};

const styles = {
  container: {
    padding: 16,
    fontFamily: "Arial, sans-serif",
    maxWidth: 800,
    margin: "0 auto",
  },
  heading: { 
    fontSize: 20, 
    textAlign: "center", 
    marginBottom: 8 
  },
  store: { 
    textAlign: "center", 
    fontSize: 13, 
    marginBottom: 12 
  },
  empty: { 
    textAlign: "center", 
    fontSize: 14, 
    color: "#666",
    marginTop: 20 
  },
  searchRow: {
    display: "flex",
    marginBottom: 12,
    gap: 8,
    justifyContent: "center",
  },
  searchInput: {
    padding: 6,
    width: 200,
    fontSize: 13,
    border: "1px solid #ddd",
    borderRadius: 4,
  },
  searchBtn: {
    padding: "6px 12px",
    fontSize: 13,
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    marginBottom: 16,
  },
  th: {
    borderBottom: "1px solid #ccc",
    padding: 8,
    background: "#f8f8f8",
    textAlign: "left",
  },
  td: {
    padding: 8,
    verticalAlign: "middle",
    borderBottom: "1px solid #eee",
  },
  image: {
    width: 50,
    height: 50,
    objectFit: "cover",
    borderRadius: 4,
    border: "1px solid #eee",
  },
  details: {
    lineHeight: 1.5,
    fontSize: 13,
  },
  qtyInput: {
    width: 50,
    padding: 4,
    fontSize: 13,
    border: "1px solid #ddd",
    borderRadius: 4,
    textAlign: "center",
  },
  deleteBtn: {
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    padding: "4px 8px",
    fontSize: 12,
    cursor: "pointer",
    borderRadius: 4,
    transition: "background-color 0.2s",
  },
  total: {
    textAlign: "right",
    marginTop: 10,
    fontSize: 14,
    fontWeight: "bold",
  },
  btnWrap: {
    display: "flex",
    justifyContent: "center",
    marginTop: 16,
  },
  submitBtn: {
    padding: "10px 20px",
    backgroundColor: "#28a745",
    color: "white",
    fontSize: 14,
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "background-color 0.2s",
  },
  spinner: {
    display: "inline-block",
    width: 12,
    height: 12,
    border: "2px solid rgba(255,255,255,0.3)",
    borderRadius: "50%",
    borderTopColor: "#fff",
    animation: "spin 1s ease-in-out infinite",
  },
  "@keyframes spin": {
    to: { transform: "rotate(360deg)" },
  },
};

export default ScannedResult;